<template>
    <div class="SchemaBuilder">
        <DataGrid
            ref="dataGrid"
            class="DataGrid"
            v-bind="dataGridConfig"
            :loading="isFetching"
            :columns="data.columns || []"
            :rows="data.rows || []"
            @onRowDoubleClicked="handleDoubleClickRow"
        />

        <Modal
            :opened="[modals.CREATE, modals.UPDATE].includes(modal)"
            :title="$route.meta.breadcrumb"
            class="ModalForm"
            #body
            @close="handleCleanModal"
        >
            <EntityForm
                :node="((node || {}).data || {}).datum"
                :selectGroups="selectGroups"
                :action="modal"
                :entityKey="entityKey"
                @onReset="handleCleanModal"
                @onSubmit="handleSubmit"
                @onError="handleCleanModal"
            />
        </Modal>

        <ModalRequest
            :opened="modal === modals.SAVE_ORDER"
            :title="$t('schema.modals.saveOrder.title')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isLoading"
            @close="handleCleanModal"
            @onAccept="saveOrder"
        />

        <ModalRequest
            :opened="modal === modals.REMOVE"
            :title="$t('entities.modals.remove.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isLoading"
            @close="handleCleanModal"
            @onAccept="removeEntity"
        />
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest, rethrowError, notifyError, notifySuccess,
} from '@/api'
import GridEntity from '@/entities/grid'
import { DataGrid, ModalRequest, Modal } from '@/components'
import { EntityForm } from './components'
import { transformRequestData } from './utils'

const DATAGRID_CONFIG = {
    hasInputFilter: false,
    filterable: false,
    sideBar: false,
    cleanFiltersButton: false,
    fitResponsive: true,
    editable: false,
    ...GridEntity.draggableConfig,
}

const MODALS = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    REMOVE: 'REMOVE',
    SAVE_ORDER: 'SAVE_ORDER',
}

export default {
    name: 'SchemaBuilder',
    components: {
        DataGrid,
        ModalRequest,
        Modal,
        EntityForm,
    },
    props: {
        entityKey: VueTypes.string,
        rows: VueTypes.any,
        selectGroups: VueTypes.any,
        matchRules: VueTypes.any,
        isFetching: VueTypes.bool.def(false),
    },
    data () {
        return {
            modal: undefined,
            modals: MODALS,

            node: undefined,
            isLoading: false,

            dataGridConfig: {
                ...DATAGRID_CONFIG,
                contextMenu: this.contextMenu,
            },
        }
    },
    computed: {
        data () {
            return transformRequestData(this.rows)
        },
    },
    methods: {
        contextMenu ({ node, api }) {
            const rows = api.getSelectedNodes()
            const multiple = (rows || []).length > 1
            const renderIcon = icon => `<i class="WIcon icon-${icon} ag-icon" />`

            return [
                'copy',
                'paste',
                'separator',
                ...(!multiple ? [
                    {
                        name: this.$t('entities.actions.edit'),
                        action: () => this.handleRequest(MODALS.UPDATE, node),
                        icon: renderIcon('edit'),
                    },
                    {
                        name: this.$t('entities.actions.delete'),
                        action: () => this.handleRequest(MODALS.REMOVE, node),
                        icon: renderIcon('trash'),
                    },
                    'separator',
                ] : []),
                {
                    name: this.$t('entities.actions.export'),
                    action: () => api.exportDataAsCsv({ columnSeparator: '\t' }),
                    icon: renderIcon('download'),
                },
            ]
        },
        handleRequest (modal, node) {
            this.node = node
            if (modal) this.modal = modal
        },
        handleCleanModal () {
            this.modal = undefined
        },
        handleDoubleClickRow (node) {
            this.handleRequest(this.modals.UPDATE, node)
        },
        handleSubmit () {
            this.$emit('onFetchData')
            this.handleCleanModal()
        },
        onNewRow () {
            this.handleRequest(this.modals.CREATE)
        },
        onSaveOrder () {
            this.handleRequest(this.modals.SAVE_ORDER)
        },
        async saveOrder () {
            this.isLoading = true
            const { gridApi } = this.$refs.dataGrid || {}
            const order = GridEntity.getFieldNodes(gridApi, 'key')
            const params = {
                entityKey: this.entityKey,
                params: { order },
            }
            apiRequest(Api().entities.order(params))
                .then(() => notifySuccess(this.$t('schema.modals.saveOrderSuccess'))())
                .catch(rethrowError(notifyError()))
                .finally(() => {
                    this.handleCleanModal()
                    this.isLoading = false
                })
        },
        async removeEntity () {
            this.isLoading = true
            const { data } = this.node
            apiRequest(Api().entities.delete(this.entityKey, data.key))
                .then(() => this.$refs.dataGrid.removeRow(this.node))
                .catch(rethrowError(notifyError()))
                .finally(() => {
                    this.handleCleanModal()
                    this.isLoading = false
                })
        },
    },
}
</script>
<style lang="scss" scoped>
.ModalForm {
    &::v-deep .Dialog {
        max-width: 800px;
    }
}
</style>
